<div class="support-wrap">

  <!-- Page Header -->
  <div class="page-header">
    <div class="header-text">
      <div class="page-eyebrow">Support</div>
      <h1 class="page-title">Contact Us</h1>
      <p class="page-desc">
        Send an issue directly to the dev team. Your account info is attached
        automatically — just describe what happened and we'll follow up by email.
      </p>
    </div>

    <div class="header-right">
      <!-- Glyph -->
      <div class="header-glyph" aria-hidden="true">
        <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="40" cy="32" r="18" stroke="rgba(96,165,250,0.30)" stroke-width="1.5"/>
          <path d="M28 32c0-6.6 5.4-12 12-12s12 5.4 12 12v4c0 3.3-2.7 6-6 6h-1" stroke="rgba(96,165,250,0.50)" stroke-width="1.5" stroke-linecap="round"/>
          <rect x="24" y="34" width="8" height="10" rx="4" stroke="rgba(96,165,250,0.40)" stroke-width="1.5"/>
          <rect x="48" y="34" width="8" height="10" rx="4" stroke="rgba(96,165,250,0.40)" stroke-width="1.5"/>
          <path d="M40 54v6M34 60h12" stroke="rgba(96,165,250,0.30)" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </div>

      <!-- User card -->
      <div class="user-card" *ngIf="userInfo as u">
        <div class="user-card-label">Submitting as</div>
        <div class="user-row">
          <span class="label">Name</span>
          <span class="value">{{ u.displayName }}</span>
        </div>
        <div class="user-row">
          <span class="label">Email</span>
          <span class="value">{{ u.email }}</span>
        </div>
      </div>
    </div>
  </div>

  <!-- Form Body -->
  <div class="support-body">
    <form class="support-form" [formGroup]="form" (ngSubmit)="submit()">

      <div class="form-section-title">Ticket Details</div>

      <div class="form-grid">

        <!-- Subject -->
        <div class="field span-2">
          <label>Subject</label>
          <input formControlName="subject" type="text" placeholder="Brief summary of the issue" />
          <div class="field-error" *ngIf="f['subject']?.touched && f['subject']?.invalid">
            Please enter a subject (max 120 characters).
          </div>
        </div>

        <!-- Category -->
        <div class="field">
          <label>Category</label>
          <select formControlName="category">
            <option *ngFor="let c of categories" [value]="c">{{ c }}</option>
          </select>
        </div>

        <!-- Severity -->
        <div class="field">
          <label>Severity</label>
          <select formControlName="severity">
            <option *ngFor="let s of severities" [value]="s">{{ s }}</option>
          </select>
          <div class="hint">Use "Urgent" only if it blocks work or affects production.</div>
        </div>

        <!-- Description -->
        <div class="field span-2">
          <label>Description</label>
          <textarea formControlName="description"
                    rows="7"
                    placeholder="What happened? What were you trying to do? Include any error messages you saw."></textarea>
          <div class="field-error" *ngIf="f['description']?.touched && f['description']?.invalid">
            Please enter at least 20 characters (max 4000).
          </div>
        </div>

        <!-- Optional details -->
        <details class="advanced span-2">
          <summary>
            <span class="summary-icon">
              <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
            Optional details <span class="summary-hint">(recommended)</span>
          </summary>

          <div class="advanced-grid">
            <div class="field">
              <label>Steps to reproduce</label>
              <textarea formControlName="stepsToReproduce" rows="4" placeholder="1) ... 2) ... 3) ..."></textarea>
            </div>
            <div class="field">
              <label>Expected result</label>
              <textarea formControlName="expectedResult" rows="4" placeholder="What you expected to happen"></textarea>
            </div>
            <div class="field">
              <label>Actual result</label>
              <textarea formControlName="actualResult" rows="4" placeholder="What actually happened"></textarea>
            </div>
            <div class="field">
              <label>Module <span class="label-optional">(optional)</span></label>
              <input formControlName="module" type="text" placeholder="FracX / ORMS / WDX / Reporting / etc." />
            </div>
            <div class="field span-2">
              <label>Page URL <span class="label-optional">(auto-filled)</span></label>
              <input formControlName="pageUrl" type="text" />
              <div class="hint">Helps us reproduce in the same area of the app.</div>
            </div>
          </div>
        </details>

        <!-- Attachments -->
        <div class="attachments span-2">
          <div class="attachments-top">
            <div>
              <div class="attachments-title">
                <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13.5 8.5l-5.5 5.5a4 4 0 0 1-5.657-5.657l6.364-6.364a2.5 2.5 0 0 1 3.535 3.535L5.879 11.85a1 1 0 0 1-1.414-1.414L10.5 4.4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
                </svg>
                Attachments <span class="label-optional">(optional)</span>
              </div>
              <div class="attachments-subtitle">
                Screenshots or logs help us resolve issues faster. Max {{ maxFiles }} files, {{ maxFileSizeMb }}MB each.
              </div>
            </div>
            <label class="upload-btn">
              <input type="file" (change)="onFilesSelected($event)" accept=".pdf,.png,.jpg,.jpeg,.gif" multiple />
              <span>
                <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 11V3M4 7l4-4 4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M3 13h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
                Add files
              </span>
            </label>
          </div>

          <div class="chips" *ngIf="attachments.length">
            <div class="chip" *ngFor="let file of attachments; let i = index">
              <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2 1h6l2 2v8H2V1z" stroke="currentColor" stroke-width="1" stroke-linejoin="round"/>
              </svg>
              <span class="chip-name">{{ file.name }}</span>
              <button type="button" class="chip-x" (click)="removeAttachment(i)" aria-label="Remove">
                <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2 2l8 8M10 2L2 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Alerts -->
        <div class="alerts span-2">
          <div class="alert success" *ngIf="submitSuccess">
            <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.3"/>
              <path d="M5 8l2.5 2.5L11 5.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Sent! A developer will follow up by email.
          </div>
          <div class="alert error" *ngIf="errorMessage">
            <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.3"/>
              <path d="M8 5v3.5M8 10.5v.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
            {{ errorMessage }}
          </div>
        </div>

        <!-- Submit -->
        <div class="actions span-2">
          <button class="btn-primary" type="submit" [disabled]="submitting || !userInfo">
            <svg *ngIf="!submitting" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2 8l5 5L14 3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <svg *ngIf="submitting" class="spin" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.5" stroke-dasharray="20 18" stroke-linecap="round"/>
            </svg>
            <span>{{ submitting ? 'Sending…' : 'Send to Developers' }}</span>
          </button>
          <div class="fineprint">
            Creates a support ticket emailed to the WellChron dev team with your account context attached.
          </div>
        </div>

      </div>
    </form>
  </div>

</div>
